import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import Layout from './Layout';
import ErrorBoundary from './ErrorBoundary';
import LoadingPage from './LoadingPage';
import RequireAuth from './RequireAuth';

/**
 * Each screen is code-split into its own chunk via `React.lazy`. Vite's
 * rollup output emits one `Screen-<hash>.js` per entry, which keeps the
 * initial bundle well under the 500 kB gzip warning — see vite.config
 * `manualChunks` for the vendor bucket that complements these lazy
 * imports.
 */
const Dashboard = lazy(() => import('@/screens/Dashboard/Dashboard'));
const Scenarios = lazy(() => import('@/screens/Scenarios/Scenarios'));
const Replays = lazy(() => import('@/screens/Replays/Replays'));
const Alerts = lazy(() => import('@/screens/Alerts/Alerts'));
const Divergence = lazy(() => import('@/screens/Divergence/Divergence'));
const RuleDrift = lazy(() => import('@/screens/RuleDrift/RuleDrift'));
const ModelRegistry = lazy(() => import('@/screens/ModelRegistry/ModelRegistry'));
const QuorumKeys = lazy(() => import('@/screens/QuorumKeys/QuorumKeys'));
const Attestations = lazy(() => import('@/screens/Attestations/Attestations'));
const AuditPackages = lazy(() => import('@/screens/AuditPackages/AuditPackages'));
const Integrations = lazy(() => import('@/screens/Integrations/Integrations'));
const Coverage = lazy(() => import('@/screens/Coverage/Coverage'));
const Audit = lazy(() => import('@/screens/Audit/Audit'));
const FieldNotes = lazy(() => import('@/screens/FieldNotes/FieldNotes'));
const Settings = lazy(() => import('@/screens/Settings/Settings'));
const Team = lazy(() => import('@/screens/Team/Team'));
const Auth = lazy(() => import('@/screens/Auth/Auth'));

/**
 * Root router. All 17 design screens (ADR-0013) are wired.
 */
export default function App() {
  return (
    <Routes>
      {/* /auth renders without the Layout chrome (no sidebar / topbar). */}
      <Route
        path="/auth"
        element={
          <ErrorBoundary>
            <Suspense fallback={<LoadingPage />}>
              <Auth />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        element={
          <ErrorBoundary>
            <RequireAuth>
              <Layout />
            </RequireAuth>
          </ErrorBoundary>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/dashboard"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Dashboard />
            </Suspense>
          }
        />
        <Route
          path="/scenarios"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Scenarios />
            </Suspense>
          }
        />
        <Route
          path="/replays"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Replays />
            </Suspense>
          }
        />
        <Route
          path="/alerts"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Alerts />
            </Suspense>
          }
        />
        <Route
          path="/divergence"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Divergence />
            </Suspense>
          }
        />
        <Route
          path="/rule-drift"
          element={
            <Suspense fallback={<LoadingPage />}>
              <RuleDrift />
            </Suspense>
          }
        />
        <Route
          path="/model-registry"
          element={
            <Suspense fallback={<LoadingPage />}>
              <ModelRegistry />
            </Suspense>
          }
        />
        <Route
          path="/quorum-keys"
          element={
            <Suspense fallback={<LoadingPage />}>
              <QuorumKeys />
            </Suspense>
          }
        />
        <Route
          path="/attestations"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Attestations />
            </Suspense>
          }
        />
        <Route
          path="/audit-packages"
          element={
            <Suspense fallback={<LoadingPage />}>
              <AuditPackages />
            </Suspense>
          }
        />
        <Route
          path="/integrations"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Integrations />
            </Suspense>
          }
        />
        <Route
          path="/coverage"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Coverage />
            </Suspense>
          }
        />
        <Route
          path="/audit"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Audit />
            </Suspense>
          }
        />
        <Route
          path="/field-notes"
          element={
            <Suspense fallback={<LoadingPage />}>
              <FieldNotes />
            </Suspense>
          }
        />
        <Route
          path="/settings"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Settings />
            </Suspense>
          }
        />
        <Route
          path="/team"
          element={
            <Suspense fallback={<LoadingPage />}>
              <Team />
            </Suspense>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function NotFound() {
  return (
    <div style={{ padding: 40 }}>
      <div className="eyebrow">§ 404</div>
      <h1 className="serif" style={{ fontSize: 40, margin: '10px 0 0' }}>
        Not found
      </h1>
      <p style={{ color: 'var(--slate-500)', fontSize: 14, marginTop: 12 }}>
        That page does not exist. See ADR-0013 for the screen inventory.
      </p>
    </div>
  );
}
